/**
 * @fileoverview WEB-713 / WEB-854 / WEB-855 — the people module: who put this
 * event on.
 *
 * One `ModuleRowList` roster, ordered last on every category lexicon. This
 * file never reads a category slug and never hardcodes a heading —
 * `module.title` (already resolved by `resolveEventModules` from
 * `config/eventLexicon.ts`) is the only heading source, which is why WEB-855
 * could collapse three per-category headings into one shared "Organized by"
 * and widen the slot from three lexicons to all eleven without touching a
 * line of this component.
 *
 * The rows come from {@link derivePeopleRows} in `@/lib/eventDetail/peopleRows`,
 * which is also what `hasModuleContent` gates the slot on — the same
 * arrangement `GenericModule` has with `resolveGenericModuleRows`. That single
 * row source is the point: `module.hasContent` stays AUTHORITATIVE (WEB-700)
 * and this file never re-derives it.
 *
 * WEB-713 shipped this module inert, because `EventDetailV2` carried no
 * organizer field at the time and `EventDetailArtist.billing` had no value
 * meaning "non-performing contributor" — so the gate resolved `false`
 * unconditionally and Eventbrite community listings rendered an empty
 * "Organizers" module while their own `/e/` page named the host in plain
 * sight. WEB-854 supplied the missing half end to end (extract → persist →
 * serve), so the organizer source is now live; the contributor source remains
 * inert until a contract widens `billing`.
 */

import type { ReactElement } from 'react';

import { EventModuleSection, ModuleRowList } from '@/components/organisms/EventModuleSection';
import { derivePeopleRows } from '@/lib/eventDetail/peopleRows';

import type { EventModuleProps } from './registry';

export {
  derivePeopleRows,
  initialsFromName,
  type PeopleModuleArtist,
  type PeopleModuleOrganizer,
} from '@/lib/eventDetail/peopleRows';

/**
 * The event's host roster. `module.hasContent` is authoritative (WEB-700);
 * this component never re-derives it from `event`. The row-length check below
 * is belt-and-braces for a caller that hand-builds a `module`, not a second
 * opinion on the gate.
 *
 * Nor does it decide whether the slot BELONGS on the page — WEB-855 put that
 * question in `isModuleSlotApplicable`, which drops the module for sources
 * that never publish an organizer, so this component is never asked to render
 * one at all.
 */
export function PeopleModule({ module, event, chroma }: EventModuleProps): ReactElement | null {
  if (!module.hasContent) {
    return null;
  }

  const rows = derivePeopleRows(event);
  if (rows.length === 0) {
    return null;
  }

  return (
    <EventModuleSection module={module} chroma={chroma}>
      <ModuleRowList rows={rows} chroma={chroma} />
    </EventModuleSection>
  );
}
